// Copyright (c) 2026 Endel Dreyer.
//
// This software is released under the MIT License.
// https://opensource.org/license/MIT
//
// colyseus.js@0.18.2
/**
 * Debug channel — the order-independent handshake between the SDK core and the
 * optional `@colyseus/sdk/debug` overlay.
 *
 * Publishers (a joined room in {@link Client}, a `Predict` instance) call
 * {@link publishDebug} whenever there's something the overlay could surface. If
 * the overlay is already loaded it renders immediately; if it loads LATER — the
 * usual dev-only `import("@colyseus/sdk/debug")` races the first room join — a
 * lightweight buffering stub captures the publishes so the overlay REPLAYS them
 * on install. No app-side ordering (awaiting the import) is required, and a
 * publisher that fires before the overlay loads is never dropped.
 *
 * Production (overlay never imported) stays effectively free: the stub is created
 * only on first publish, holds WeakRefs so nothing is pinned, and is bounded — an
 * app that never loads the overlay leaks neither memory nor room references.
 */
// Old embedded runtimes (some Cocos/Construct targets) lack WeakRef; fall back to
// a strong ref there — still bounded, so the worst case is a few pinned handles in
// a debug-less session, never an unbounded leak.
const makeRef = typeof WeakRef !== "undefined" ? (h) => new WeakRef(h) : (h) => ({ deref: () => h });
// A handful of rooms/Predict handles is plenty of replay depth; the bound keeps a
// debug-less prod session from growing the buffer without limit.
const MAX_BUFFERED = 64;
/**
 * Publish a `handle` to the overlay `channel` (`"room"` | `"predict"` | …).
 * Prod fast path: one property read, then either a live `publish` (overlay
 * loaded) or a bounded WeakRef append (buffered for replay on install).
 */
function publishDebug(channel, handle) {
    const g = globalThis;
    let reg = g.__colyseusDebug;
    if (!reg) {
        const buffer = [];
        reg = g.__colyseusDebug = {
            __buffer: buffer,
            publish(ch, h) {
                buffer.push([ch, makeRef(h)]);
                if (buffer.length > MAX_BUFFERED) {
                    buffer.shift();
                }
            },
        };
    }
    reg.publish(channel, handle);
}
/**
 * True only when the overlay installed its LIVE receiver — NOT when
 * {@link publishDebug} merely created the pre-load buffer. Diagnostics that must
 * stay free in production (reconciler drift bookkeeping) gate on this.
 */
function debugOverlayActive() {
    if (typeof globalThis === "undefined") {
        return false;
    }
    const reg = globalThis.__colyseusDebug;
    return reg != null && reg.__buffer === undefined;
}

export { debugOverlayActive, publishDebug };
//# sourceMappingURL=debug-channel.mjs.map
